import moment from 'moment';
import { humanize } from '@utils';
import { FONT_FAMILY } from '@utils/constants';
import { findAxisIndex } from '@components/metrics-panel-hz/hooks/utils';
import { TIME_AXIS, VALUE_AXIS } from '@components/metrics-panel-hz/utils/lineChartInputSchema';
import { formatAlertSummary } from '@components/alert-panel/AIAlert/hooks/utils';
import { set } from 'lodash';
import { AI_ALERT_EVENTS, AI_ALERT_MARKERS, AI_ALERT_PCP, AiAlertSeriesKeys, DEVICE_AXIS, PCP_AXIS } from './constants';
import { deviceSeriesKeys } from '@src/utils/aiAlerts/mapping';


export const legendSeries: any = [
  'All except anomaly',
  'Projected',
  'T-7',
  'Threshold',
  'Baseline',
  'PCP Drop',
  'PCT Drop',
  'Cumulative Impacted Unique Devices',
  'Total Cumulative Unique Devices',
].map((name) => ({ name }));

const getMarker = (color) => `<span style="
display: inline-block;
background-color: ${color};
width: 7px;
height: 14px;
position: relative;
top: 2px;
"></span>`;

export const eventsMarkLineOptions = (data) => {
  return {
    symbol: ['none', 'rect'],
    lineStyle: {
      width: 1,
      type: 'solid',
    },
    label: {
      show: false,
    },
    emphasis: {
      disabled: true,
    },
    tooltip: {
      trigger: 'item',
      // position: ['5%', '10%'],
      borderWidth: 0,
      confine: true,
      textStyle: {
        fontFamily: FONT_FAMILY,
        // width: 100,
        // height: 40,
        fontSize: 12,
        overflow: 'break',
        shadowBlur: 10,
        shadowColor: 'rgba(0, 0, 0, .2)',
        shadowOffsetX: 1,
        shadowOffsetY: 2,
        borderRadius: 4,
      },
      formatter: (params) => {
        const { alertEvent } = params.data;
        const marker = getMarker(params.borderColor);

        if (alertEvent) {
          return `
          ${marker}
          <strong> Event: ${alertEvent.eventType} </strong> <br/>
          <i style="color: #4a4a4a;">Value: ${alertEvent.peakValue}</i> <br/>
          <i style="color: #4a4a4a;">Cumulative Impacted Unique Devices: ${alertEvent.cumulativeImpactedUniques}</i> <br/>
          <i style="color: #4a4a4a;">Event Time: ${alertEvent.eventTime}</i> <br/>`;
        }

        return `<strong> Event: ${params.data.name} </strong>`;
      },
    },
    data,
  };
};

export const deviceAxis = {
  id: DEVICE_AXIS,
  type: 'value',
  show: false,
  splitNumber: 1,
  nameLocation: 'middle',
  position: 'right',
  axisLabel: {
    color: '#000',
    fontSize: 12,
    fontFamily: FONT_FAMILY,
    hideOverlap: true,
    showMinLabel: true,
    showMaxLabel: true,
    formatter: (value) => humanize(value),
  },
  splitLine: { show: true },
  axisTick: { show: false },
  axisLine: {
    show: false,
    lineStyle: {
      color: '#ccc',
    },
  },
};

export const pcpDropAxis = {
  id: PCP_AXIS,
  type: 'value',
  min: 0,
  max: 100,
  interval: 50,
  splitNumber: 1,
  nameLocation: 'middle',
  position: 'right',
  show: false,
  axisLabel: {
    color: '#000',
    fontSize: 12,
    fontFamily: FONT_FAMILY,
    formatter: '{value}%',
    hideOverlap: true,
  },
  splitLine: { show: true },
  axisTick: { show: true },
  axisLine: {
    show: false,
    lineStyle: {
      color: '#ccc',
    },
  },
};

// reset alert series data/markline
export const resetAlertOption = (currentSeries) => {
  const alertSeies: any = [];
  currentSeries.forEach((item) => {
    if (AiAlertSeriesKeys.includes(item.id)) {
      if (item.id === AI_ALERT_EVENTS) {
        set(item, ['markLine', 'data'], []);
      } else {
        item.data = [];
      }
      alertSeies.push(item);
    }
  });

  return alertSeies;
};

export const getAlertsOption = ({ alert, xAxis, yAxis }) => {
  const { graphSeries, severityBrushLine, uniqueDevicesSeries, aiAlertKey } = alert;
  const isPcpAlert = aiAlertKey === 'AUTOALERTPCP' || aiAlertKey === 'APP_ACTIVE_DEVICES';

  const xAxisIndex = findAxisIndex(xAxis, TIME_AXIS);
  const yAxisIndex = findAxisIndex(yAxis, VALUE_AXIS);

  const alertSeries: any = [
    {
      id: AI_ALERT_EVENTS,
      name: 'Alert events',
      type: 'line',
      markLine: eventsMarkLineOptions(severityBrushLine.data),
      xAxisIndex,
      yAxisIndex,
    },
  ];

  graphSeries.forEach((series) => {
    const { seriesType } = series;
    if (AiAlertSeriesKeys.includes(seriesType)) {
      alertSeries.push({
        ...series,
        id: seriesType,
        xAxisIndex,
        yAxisIndex: seriesType === AI_ALERT_PCP ? findAxisIndex(yAxis, PCP_AXIS) : yAxisIndex,
      });
    }
  });

  // unique devices series
  if (!isPcpAlert) {
    alertSeries.push(
      ...uniqueDevicesSeries.map((ud) => ({
        ...ud,
        xAxisIndex,
        yAxisIndex: findAxisIndex(yAxis, DEVICE_AXIS),
      })),
    );
  }

  return alertSeries;
};

// unit mapping for tooltip formatter;
export const alertSeriesUnitMapping = {
  [AI_ALERT_PCP]: '%',
};
// # for device series
deviceSeriesKeys.forEach((seriesKey) => {
  alertSeriesUnitMapping[seriesKey] = '';
});

export const alertsmarkLineOptions = (data) => {
  return {
    symbol: ['none', 'diamond'],
    lineStyle: {
      width: 2,
      type: 'solid',
      color: '#CE6940',
      opacity: 0.5,
    },
    label: {
      show: false,
    },
    emphasis: {
      disabled: true,
    },
    tooltip: {
      trigger: 'item',
      // position: ['5%', '10%'],
      borderWidth: 0,
      confine: true,
      textStyle: {
        fontFamily: FONT_FAMILY,
        // width: 100,
        // height: 40,
        fontSize: 12,
        overflow: 'break',
        shadowBlur: 10,
        shadowColor: 'rgba(0, 0, 0, .2)',
        shadowOffsetX: 1,
        shadowOffsetY: 2,
        borderRadius: 4,
      },
      formatter: ({ data }) => {
        const { info } = data;
        return `
          <div style="color: #4a4a4a;">
          <strong> AI Alert: ${info.alertId} </strong> <br/>
          <span>Time Alert Fired: ${info.timeAlertFired}</span> <br/>
          <span>Root Cause: ${info.rootCause}</span> <br/>
          ${'peakValue' in info ? `<span>Peak Value: ${info.peakValue}</span> <br/>` : ''}
          ${
            'cumulativeImpactedUniques' in info
              ? `<span>Cumulative Impacted Unique Devices ${info.cumulativeImpactedUniques}</span>`
              : ''
          }
          </div>`;
      },
    },
    data: data,
  };
};

// chart markLine format
export const formatAlertMark = (alert) => {
  const fomattedEvent = {
    aiAlert: alert,
    // markline point
    xAxis: moment(alert.eventTimeRaw).format(),
    // for tooltip
    info: formatAlertSummary(alert),
    // lineStyle: alert.alertId === selectedAlertId ? { opacity: 0 } : { opacity: 0.5 }
  };
  return fomattedEvent;
};

export const getAlertMarkersOption = ({ alerts, selectedAlertId }, params = {}) => {
  // hide selected alert as the line would be marked by the first event
  const formattedAlerts: any = [];
  alerts.forEach((alert) => {
    if (alert.alertId !== selectedAlertId) {
      formattedAlerts.push(formatAlertMark(alert));
    }
  });

  return {
    id: AI_ALERT_MARKERS,
    name: 'AI alerts',
    type: 'line',
    markLine: alertsmarkLineOptions(formattedAlerts),
    xAxisIndex: 0,
    yAxisIndex: 0,
    ...params,
  };
};
